import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { getWalletDetail, type WalletDetail } from './api';
import { Card, CardBody } from '../../components/ui/Card';

export function WalletDetailPage() {
  const { walletID = '' } = useParams<{ walletID: string }>();
  const { org } = useCurrentOrg();
  const orgId = org?.id ?? '';

  const detail = useQuery({
    queryKey: ['wallet-detail', orgId, walletID],
    queryFn: () => getWalletDetail(orgId, walletID),
    enabled: !!orgId && !!walletID,
    staleTime: 30_000,
  });

  if (!org) return null;

  return (
    <div className="space-y-8">
      <Link to="/wallets" className="text-sm text-ink-secondary hover:text-ink-primary">
        &larr; All wallets
      </Link>

      {detail.data?.status === 'deprecated' && (
        <DeprecatedBanner detail={detail.data} />
      )}

      {detail.data && <WalletHeader wallet={detail.data} />}
      {detail.isLoading && (
        <div className="rounded-xl border border-line-soft bg-surface-white p-8">
          <p className="text-sm text-ink-tertiary">Loading wallet details...</p>
        </div>
      )}
    </div>
  );
}

function DeprecatedBanner({
  detail,
}: {
  detail: { address: string; replacement_wallet_id?: string };
}) {
  return (
    <div className="rounded-xl border border-amber-300/60 bg-amber-50/70 p-5 text-sm text-amber-900">
      <p className="font-medium">This wallet is archived.</p>
      <p className="mt-1 text-amber-800">
        Address <span className="font-mono text-xs">{detail.address}</span> has
        been replaced by a newer wallet. Its on-chain history may be empty
        because the user moved to a new address.
      </p>
      {detail.replacement_wallet_id && (
        <p className="mt-3">
          <Link
            to={`/wallets/${detail.replacement_wallet_id}`}
            className="font-medium text-amber-900 underline-offset-4 hover:underline"
          >
            Open the user's active wallet &rarr;
          </Link>
        </p>
      )}
    </div>
  );
}

function WalletHeader({ wallet }: { wallet: WalletDetail }) {
  const curve = wallet.curve || 'secp256k1';
  const created = new Date(wallet.created_at).toLocaleDateString();
  const statusOk = wallet.status === 'active';

  return (
    <Card>
      <CardBody>
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-tertiary">
                Address
              </p>
              <p className="mt-1 font-mono text-sm text-ink-primary" title={wallet.address}>
                {wallet.address}
              </p>
            </div>
            <span
              className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 font-mono text-[11px] font-medium uppercase tracking-eyebrow ${
                statusOk ? 'bg-ok/10 text-ok' : 'bg-surface-soft text-ink-tertiary'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${statusOk ? 'bg-ok' : 'bg-ink-muted'}`} />
              {wallet.status}
            </span>
          </div>

          <div className="grid gap-4 sm:grid-cols-4">
            <DetailField label="Curve" value={curve} />
            <DetailField label="Networks" value={wallet.networks?.join(', ') ?? wallet.chain_type} />
            <DetailField label="Created" value={created} />
          </div>

          {wallet.signer_address && (
            <div>
              <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-tertiary">
                Signer address
              </p>
              <p className="mt-1 font-mono text-xs text-ink-secondary">
                {wallet.signer_address}
              </p>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-tertiary">{label}</p>
      <p className="mt-1 text-sm font-medium text-ink-primary">{value}</p>
    </div>
  );
}
